import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CCard, CCardBody } from '@coreui/react-pro'
import PlatformAnalyticsService from 'src/service/PlatformAnalyticsService'
import { MAX_RANGE_DAYS, daysBetween, rangeForPreset } from '../AnalyticsV2/FilterBar'
import PlatformFilterBar from './PlatformFilterBar'
import OverviewPanel from './OverviewPanel'
import GamesPanel from './GamesPanel'
import UsagePanel from './UsagePanel'
import UsersPanel from './UsersPanel'
import LivePanel from './LivePanel'
import UserDetailOffcanvas from './UserDetailOffcanvas'
import ScreenGamesOffcanvas from './ScreenGamesOffcanvas'
import GameDetailOffcanvas from './GameDetailOffcanvas'
import { errorMessage } from './platformHelpers'
import '../AnalyticsV2/AppAnalytics.css'
import './PlatformAnalytics.css'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'live', label: 'Live' },
  { key: 'games', label: 'Games' },
  { key: 'users', label: 'App Users' },
  { key: 'usage', label: 'App Usage' },
]

const DEFAULT_PRESET = '28d'
const LIVE_POLL_MS = 30000

/** Filters live in the URL so a view can be bookmarked or shared. */
const readFilters = (search) => {
  const params = new URLSearchParams(search)
  const preset = params.get('preset') || DEFAULT_PRESET
  let from = params.get('from')
  let to = params.get('to')

  if (preset !== 'custom' || !from || !to) {
    const range = rangeForPreset(preset === 'custom' ? DEFAULT_PRESET : preset)
    if (range) {
      from = range.from
      to = range.to
    }
  }

  return {
    preset,
    from,
    to,
    platform: params.get('platform') || 'all',
    interval: params.get('interval') || 'day',
    tab: params.get('tab') || 'overview',
  }
}

const writeFilters = (f) => {
  const params = new URLSearchParams()
  params.set('preset', f.preset)
  if (f.preset === 'custom') {
    if (f.from) params.set('from', f.from)
    if (f.to) params.set('to', f.to)
  }
  if (f.platform !== 'all') params.set('platform', f.platform)
  if (f.interval !== 'day') params.set('interval', f.interval)
  if (f.tab !== 'overview') params.set('tab', f.tab)
  return params.toString()
}

/**
 * One request's lifecycle. A failed request is surfaced as an error, never as
 * an empty result, so a broken endpoint can't masquerade as "no activity".
 */
const useResource = (fetcher, key, enabled) => {
  const [state, setState] = useState({ data: null, meta: null, loading: enabled, error: null })

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false }))
      return undefined
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))

    fetcher()
      .then((res) => {
        if (cancelled) return
        if (res?.success) setState({ data: res.data, meta: res.meta, loading: false, error: null })
        else setState({ data: null, meta: null, loading: false, error: errorMessage(res) })
      })
      .catch((err) => {
        if (!cancelled)
          setState({ data: null, meta: null, loading: false, error: errorMessage(err) })
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])

  return state
}

const PlatformAnalytics = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const [filters, setFilters] = useState(() => readFilters(location.search))
  const [reloadToken, setReloadToken] = useState(0)
  const [selectedGame, setSelectedGame] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedScreen, setSelectedScreen] = useState(null)
  const [usersGameId, setUsersGameId] = useState(null)
  const [liveToken, setLiveToken] = useState(0)

  // Only one drawer at a time: opening one from inside another swaps them.
  const openGame = useCallback((game) => {
    setSelectedUser(null)
    setSelectedScreen(null)
    setSelectedGame(game)
  }, [])
  const openUser = useCallback((user) => {
    setSelectedGame(null)
    setSelectedScreen(null)
    setSelectedUser(user)
  }, [])
  const openScreen = useCallback((screen) => {
    setSelectedGame(null)
    setSelectedUser(null)
    setSelectedScreen(screen)
  }, [])
  // The usage tab is only fetched once someone opens it.
  const [usageWanted, setUsageWanted] = useState(filters.tab === 'usage')

  const spanDays = daysBetween(filters.from, filters.to)
  const rangeInvalid = !filters.from || !filters.to || spanDays < 1
  const rangeTooLong = spanDays > MAX_RANGE_DAYS
  const rangeOk = !rangeInvalid && !rangeTooLong

  const rangeError = rangeInvalid
    ? 'Choose a start date on or before the end date.'
    : rangeTooLong
    ? `Please choose a range of ${MAX_RANGE_DAYS} days or fewer. You have selected ${spanDays} days.`
    : null

  const updateFilters = useCallback(
    (next) => {
      setFilters(next)
      navigate({ search: writeFilters(next) }, { replace: true })
      if (next.tab === 'usage') setUsageWanted(true)
    },
    [navigate],
  )

  const apiFilters = useMemo(
    () => ({ from: filters.from, to: filters.to, platform: filters.platform }),
    [filters.from, filters.to, filters.platform],
  )
  const baseKey = [filters.from, filters.to, filters.platform, reloadToken].join('|')

  const overview = useResource(
    () => PlatformAnalyticsService.getOverview(apiFilters),
    baseKey,
    rangeOk,
  )
  const trends = useResource(
    () => PlatformAnalyticsService.getTrends(apiFilters, filters.interval),
    `${baseKey}|${filters.interval}`,
    rangeOk,
  )
  const games = useResource(() => PlatformAnalyticsService.getGames(apiFilters), baseKey, rangeOk)
  const usage = useResource(
    () => PlatformAnalyticsService.getUsage(apiFilters),
    baseKey,
    rangeOk && usageWanted,
  )

  // "Online now" ignores the date range and refreshes on its own.
  const [live, setLive] = useState({ data: null, loading: true, error: null })
  useEffect(() => {
    let cancelled = false
    const load = () =>
      PlatformAnalyticsService.getLive()
        .then((res) => {
          if (cancelled) return
          setLive(
            res?.success
              ? { data: res.data, loading: false, error: null }
              : { data: null, loading: false, error: errorMessage(res) },
          )
        })
        .catch(
          (err) => !cancelled && setLive({ data: null, loading: false, error: errorMessage(err) }),
        )
    load()
    const timer = setInterval(load, LIVE_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [reloadToken, liveToken])

  const retry = () => setReloadToken((t) => t + 1)

  const showGameUsers = (gameId) => {
    setSelectedGame(null)
    setUsersGameId(gameId)
    updateFilters({ ...filters, tab: 'users' })
  }
  const meta = overview.meta || trends.meta || games.meta

  const tabCounts = {
    overview: null,
    live: live.data ? live.data.online_users : null,
    games: games.loading ? null : games.data?.rows?.length ?? null,
    users: null,
    usage: null,
  }

  return (
    <div className="av2-page pa-page">
      <div className="ge-page-header">
        <h1 className="ge-page-title">Platform Analytics</h1>
        <p className="ge-page-subtitle">
          Every game on the platform in one place — who is using the app, which games are growing,
          and which have gone quiet.
        </p>
      </div>

      <PlatformFilterBar
        filters={filters}
        onChange={updateFilters}
        meta={meta}
        rangeError={rangeError}
        onRefresh={retry}
      />

      <div className="ge-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={filters.tab === tab.key}
            className={`ge-tab${filters.tab === tab.key ? ' active' : ''}`}
            onClick={() => updateFilters({ ...filters, tab: tab.key })}
          >
            {tab.label}
            {tabCounts[tab.key] !== null && (
              <span className="ge-tab-count">{tabCounts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {rangeError ? (
        <CCard>
          <CCardBody className="text-center py-5">
            <div className="fw-semibold mb-1">Adjust the date range to see your data</div>
            <div className="text-medium-emphasis small">{rangeError}</div>
          </CCardBody>
        </CCard>
      ) : (
        // Only the active tab is mounted — Chart.js measures its parent on init
        // and a chart created in a hidden pane comes back 0-sized.
        <>
          {filters.tab === 'overview' && (
            <OverviewPanel
              overview={overview}
              trends={trends}
              games={games}
              live={live}
              interval={filters.interval}
              onIntervalChange={(interval) => updateFilters({ ...filters, interval })}
              onOpenGame={openGame}
              onShowGames={() => updateFilters({ ...filters, tab: 'games' })}
              onRetry={retry}
            />
          )}
          {filters.tab === 'live' && (
            <LivePanel
              live={live}
              onOpenUser={openUser}
              onOpenGame={openGame}
              onRefresh={() => setLiveToken((t) => t + 1)}
            />
          )}
          {filters.tab === 'games' && (
            <GamesPanel
              games={games}
              live={live}
              onOpenGame={openGame}
              onShowUsers={showGameUsers}
              onRetry={retry}
            />
          )}
          {filters.tab === 'users' && (
            <UsersPanel
              filters={apiFilters}
              gameOptions={games.data?.rows}
              gameId={usersGameId}
              onGameChange={setUsersGameId}
              onOpenUser={openUser}
              reloadToken={reloadToken}
            />
          )}
          {filters.tab === 'usage' && (
            <UsagePanel usage={usage} onRetry={retry} onOpenScreen={openScreen} />
          )}
        </>
      )}

      <GameDetailOffcanvas
        game={selectedGame}
        filters={apiFilters}
        reloadToken={reloadToken}
        live={live}
        onClose={() => setSelectedGame(null)}
        onOpenUser={openUser}
        onOpenScreen={openScreen}
        onShowUsers={showGameUsers}
      />
      <UserDetailOffcanvas
        user={selectedUser}
        filters={apiFilters}
        reloadToken={reloadToken}
        onClose={() => setSelectedUser(null)}
        onOpenGame={openGame}
      />
      <ScreenGamesOffcanvas
        screen={selectedScreen}
        filters={apiFilters}
        reloadToken={reloadToken}
        onClose={() => setSelectedScreen(null)}
        onOpenGame={openGame}
      />
    </div>
  )
}

export default PlatformAnalytics
